import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Upload, X, Loader2, GripVertical, Star, Maximize2, ChevronLeft, ChevronRight } from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  rectSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { createPortal } from "react-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Единый резолвер живёт в lib/imageUrl.js; здесь реэкспорт для обратной совместимости.
export { resolveImageUrl } from "../lib/imageUrl";
import { resolveImageUrl } from "../lib/imageUrl";

/**
 * Multi-image upload field with drag-and-drop reordering.
 * - value: array of image refs (URLs or /api/static/...)
 * - onChange: (newArray) => void
 * - maxImages: optional limit
 * - testId
 *
 * The first image in the array is treated as the cover. A "★" badge highlights it.
 */
export default function ImageUpload({ value = [], onChange, maxImages = 8, testId = "image-upload", label = "Изображения", uploadUrl = null }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  // Lightbox: index of currently-zoomed image, or -1 when closed.
  const [zoomIdx, setZoomIdx] = useState(-1);

  const items = Array.isArray(value) ? value : (typeof value === "string" && value ? [value] : []);

  // Stable IDs for sortable items. Each value may repeat in theory — append index.
  const sortableIds = items.map((img, i) => `${img}__${i}`);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortableIds.indexOf(active.id);
    const newIndex = sortableIds.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(items, oldIndex, newIndex));
  };

  const uploadFiles = async (files) => {
    setError("");
    const slots = Math.max(0, maxImages - items.length);
    const toUpload = Array.from(files).slice(0, slots);
    if (toUpload.length === 0) return;

    setUploading(true);
    try {
      const uploaded = [];
      for (const file of toUpload) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(uploadUrl || `${API}/upload/file`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.data?.url) uploaded.push(res.data.url);
      }
      onChange([...items, ...uploaded]);
    } catch (err) {
      setError(err.response?.data?.detail || "Ошибка загрузки файла");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleFilePick = (e) => {
    if (e.target.files?.length) uploadFiles(e.target.files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const removeAt = (idx) => {
    const next = [...items];
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div data-testid={testId}>
      {label && (
        <label style={{ fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>
          {label} <span style={{ color: "#9ca3af", fontWeight: 500, letterSpacing: 0, textTransform: "none" }}>({items.length}/{maxImages})</span>
        </label>
      )}

      {items.length > 1 && (
        <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#9ca3af", marginBottom: "8px", display: "flex", alignItems: "center", gap: "4px" }}>
          <GripVertical size={11} /> Перетащите фотографии, чтобы изменить порядок. Первая фотография — обложка.
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(96px,1fr))", gap: "10px" }}>
            {items.map((img, idx) => (
              <SortableImage
                key={sortableIds[idx]}
                id={sortableIds[idx]}
                img={img}
                idx={idx}
                isCover={idx === 0}
                testId={testId}
                onRemove={() => removeAt(idx)}
                onZoom={() => setZoomIdx(idx)}
              />
            ))}

            {items.length < maxImages && (
              <button type="button"
                onClick={() => inputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
                onDrop={handleDrop}
                disabled={uploading}
                data-testid={`${testId}-pick-btn`}
                style={{
                  aspectRatio: "1/1",
                  borderRadius: "6px",
                  border: "1.5px dashed #d1d5db",
                  background: uploading ? "#f3f4f6" : "#fafafa",
                  cursor: uploading ? "wait" : "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  color: "#9ca3af",
                  fontFamily: "Manrope",
                  fontSize: "11px",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => { if (!uploading) { e.currentTarget.style.borderColor = "#C5A059"; e.currentTarget.style.color = "#C5A059"; e.currentTarget.style.background = "rgba(197,160,89,0.05)"; } }}
                onMouseLeave={e => { if (!uploading) { e.currentTarget.style.borderColor = "#d1d5db"; e.currentTarget.style.color = "#9ca3af"; e.currentTarget.style.background = "#fafafa"; } }}>
                {uploading ? <><Loader2 size={20} className="spin" style={{ animation: "spin 1s linear infinite" }} /><span>Загрузка…</span></> : <><Upload size={18} /><span>Загрузить фото</span></>}
              </button>
            )}
          </div>
        </SortableContext>
      </DndContext>

      <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleFilePick} style={{ display: "none" }}
        data-testid={`${testId}-file-input`} />

      {error && <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#dc2626", marginTop: "8px" }}>{error}</div>}
      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>

      {zoomIdx >= 0 && (
        <ImageLightbox
          items={items}
          index={zoomIdx}
          onChange={setZoomIdx}
          onClose={() => setZoomIdx(-1)}
        />
      )}
    </div>
  );
}

function SortableImage({ id, img, idx, isCover, testId, onRemove, onZoom }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 1,
    position: "relative",
    aspectRatio: "1/1",
    borderRadius: "6px",
    overflow: "hidden",
    border: isCover ? "2px solid #C5A059" : "1px solid #e5e7eb",
    background: "#f9fafb",
    boxShadow: isDragging ? "0 8px 20px rgba(0,0,0,0.18)" : "none",
    cursor: "grab",
    touchAction: "none",
  };

  return (
    <div ref={setNodeRef} style={style}
         data-testid={`${testId}-item-${idx}`}
         {...attributes} {...listeners}>
      <img src={resolveImageUrl(img, { w: 300 })} alt={`upload-${idx}`}
        draggable={false} loading="lazy" decoding="async"
        style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
        onError={e => { e.target.src = ""; e.target.style.background = "#f3f4f6"; }} />

      {isCover && (
        <div data-testid={`${testId}-cover-badge-${idx}`}
             style={{ position: "absolute", top: "4px", left: "4px",
                      background: "linear-gradient(135deg,#C5A059,#D4B878)",
                      color: "#0A0A0A", borderRadius: "3px",
                      padding: "2px 6px 2px 4px",
                      display: "flex", alignItems: "center", gap: "2px",
                      fontFamily: "Manrope", fontSize: "9px", fontWeight: 700,
                      letterSpacing: "0.06em", textTransform: "uppercase" }}>
          <Star size={9} fill="currentColor" /> Обложка
        </div>
      )}

      <button type="button"
        onPointerDown={e => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        data-testid={`${testId}-remove-${idx}`}
        style={{ position: "absolute", top: "4px", right: "4px",
                 background: "rgba(0,0,0,0.75)", border: "none",
                 borderRadius: "50%", width: "22px", height: "22px",
                 display: "flex", alignItems: "center", justifyContent: "center",
                 cursor: "pointer", color: "white", zIndex: 2 }}>
        <X size={12} />
      </button>

      {/* Zoom button — opens lightbox. Full-tile cursor "grab" collides
          with plain clicks, so we expose a dedicated hit-target that blocks
          drag by stopping pointer events. */}
      <button type="button"
        onPointerDown={e => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onZoom && onZoom(); }}
        data-testid={`${testId}-zoom-${idx}`}
        title="Открыть в увеличенном виде"
        style={{ position: "absolute", bottom: "4px", right: "4px",
                 background: "rgba(0,0,0,0.75)", border: "none",
                 borderRadius: "4px", padding: "3px 5px",
                 display: "flex", alignItems: "center", gap: 3,
                 cursor: "zoom-in", color: "white", zIndex: 2,
                 fontFamily: "Manrope", fontSize: 10, fontWeight: 600 }}>
        <Maximize2 size={11} />
      </button>
    </div>
  );
}

/**
 * Fullscreen lightbox for inspecting uploaded photos.
 * • Click backdrop / press Esc → close
 * • ←/→ arrows or on-screen buttons → navigate between photos
 * • Rendered via portal so parent modals don't clip the overlay.
 */
function ImageLightbox({ items, index, onChange, onClose }) {
  const total = items.length;
  const cur = items[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && total > 1) onChange((index + 1) % total);
      else if (e.key === "ArrowLeft" && total > 1) onChange((index - 1 + total) % total);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, total, onChange, onClose]);

  return createPortal(
    <div
      data-testid="image-lightbox"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)",
               zIndex: 1300, display: "flex", alignItems: "center",
               justifyContent: "center", padding: 40 }}
    >
      <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="image-lightbox-close"
        style={{ position: "absolute", top: 20, right: 20, background: "rgba(255,255,255,0.1)",
                 border: "1px solid rgba(255,255,255,0.25)", color: "white",
                 borderRadius: "50%", width: 40, height: 40, cursor: "pointer",
                 display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
        <X size={20} />
      </button>

      {total > 1 && (
        <>
          <button type="button"
            onClick={(e) => { e.stopPropagation(); onChange((index - 1 + total) % total); }}
            data-testid="image-lightbox-prev"
            style={{ position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)",
                     background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.25)",
                     color: "white", borderRadius: "50%", width: 44, height: 44, cursor: "pointer",
                     display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
            <ChevronLeft size={22} />
          </button>
          <button type="button"
            onClick={(e) => { e.stopPropagation(); onChange((index + 1) % total); }}
            data-testid="image-lightbox-next"
            style={{ position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)",
                     background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.25)",
                     color: "white", borderRadius: "50%", width: 44, height: 44, cursor: "pointer",
                     display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
            <ChevronRight size={22} />
          </button>
        </>
      )}

      <img
        src={resolveImageUrl(cur)}
        alt={`preview-${index}`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "95vw", maxHeight: "90vh", objectFit: "contain",
                 boxShadow: "0 24px 60px rgba(0,0,0,0.6)", borderRadius: 6,
                 background: "#111" }}
      />

      {total > 1 && (
        <div style={{ position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)",
                      background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.15)",
                      color: "white", padding: "6px 14px", borderRadius: 999,
                      fontFamily: "Manrope", fontSize: 12, letterSpacing: 0.3 }}>
          {index + 1} / {total}
        </div>
      )}
    </div>,
    document.body,
  );
}
